/**
 * Reference buttons component for displaying navigation buttons in the Labels modal
 */
import React from "react"
import { Button, Popover } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faArrowUpRightFromSquare,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons"
import { LabelType, LabelItem } from "utils/labelUtils"

interface ReferenceButtonsProps {
  item: LabelItem
  scrollToLine?: (lineNumber: number) => void
  setIsModalVisible: (visible: boolean) => void
}

export const ReferenceButtons: React.FC<ReferenceButtonsProps> = ({
  item,
  scrollToLine,
  setIsModalVisible,
}) => {
  const handleNavigate = (lineNumber: number) => {
    if (scrollToLine) {
      scrollToLine(lineNumber)
      setIsModalVisible(false)
    }
  }

  if (item.type === LabelType.QUESTION) {
    // Question Labels: Show "Go to Question" button
    return scrollToLine && item.lineNumbers[0] ? (
      <Button
        className="mx-auto flex"
        icon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} />}
        iconPosition="end"
        onClick={() => handleNavigate(item.lineNumbers[0])}
      >
        Go to Question
      </Button>
    ) : (
      <span className="text-gray-400">-</span>
    )
  }

  if (item.type === LabelType.LIST || item.type === LabelType.RANGE_LIST) {
    const refCount = item.referenceLineNumbers?.length ?? 0

    // The line buttons live in a Popover (portal) so the cell stays compact and
    // the list scrolls independently of the virtual table.
    const refsPopover = (
      <div
        className="flex flex-col gap-1 max-h-[320px] overflow-y-auto pr-1"
        style={{ minWidth: 130 }}
      >
        {item.referenceLineNumbers?.map((lineNum, idx) => (
          <Button
            key={idx}
            icon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} />}
            iconPosition="end"
            onClick={() => handleNavigate(lineNum)}
          >
            Line {lineNum}
          </Button>
        ))}
      </div>
    )

    return (
      <div className="flex flex-col gap-2 items-center w-full">
        {/* Go to Definition button */}
        {scrollToLine && item.lineNumbers[0] && (
          <Button
            block
            icon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} />}
            iconPosition="end"
            onClick={() => handleNavigate(item.lineNumbers[0])}
          >
            Go to Definition
          </Button>
        )}
        {/* Reference count — opens a popover with the jump-to-line buttons */}
        {refCount > 0 ? (
          scrollToLine ? (
            <Popover
              content={refsPopover}
              title={`${refCount} reference${refCount !== 1 ? "s" : ""}`}
              trigger="click"
              placement="left"
            >
              <Button
                block
                icon={<FontAwesomeIcon icon={faChevronDown} />}
                iconPosition="end"
              >
                {refCount} ref{refCount !== 1 ? "s" : ""}
              </Button>
            </Popover>
          ) : (
            <span className="text-blue-600 font-semibold">
              {refCount} ref{refCount !== 1 ? "s" : ""}
            </span>
          )
        ) : (
          <span className="text-gray-400">0 refs</span>
        )}
      </div>
    )
  }

  if (item.type === LabelType.EXCEPTIONS) {
    // Exceptions: Show "Go to Definition" button
    return scrollToLine && item.lineNumbers[0] ? (
      <Button
        className="mx-auto flex"
        icon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} />}
        iconPosition="end"
        onClick={() => handleNavigate(item.lineNumbers[0])}
      >
        Go to Definition
      </Button>
    ) : (
      <span className="text-gray-400">-</span>
    )
  }

  // Default: Show dash
  return <span className="text-gray-400">-</span>
}
